import { Injectable, NgZone } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef, MatSnackBarConfig, SimpleSnackBar } from '@angular/material/snack-bar';
import { NgxNoticeDialogComponent } from '../components/notice-dialog/notice-dialog.component';
import { NgxNoticeInfoSnackbarComponent } from '../components/notice-info-snackbar/notice-info-snackbar.component';
import { INoticeDialogOptions, INoticeIconSnackBarOptions } from '../models/interfaces';
import { INoticeSnackBarOptions } from '../models/interfaces';
import { NotofierUtilitiesService } from './notofier-utilities.service';

/** A service for showing various types of notifications via Material style notifications, such as snackbars and dialogs. */
@Injectable({ providedIn: 'root' })
export class NotofierTemplatingService {
    constructor (
        private _zone: NgZone,
        private _utilities: NotofierUtilitiesService,
        private _snackBar: MatSnackBar,
        private _dialog: MatDialog
    ) {}

    /**
	 * Show a Material dialog using our custom notice template, configuring the Material Dialog options and passing the
	 * template configuration via the data property.
	 *
	 * This is a lower level method and in many cases `notify()` or one of the other show methods should be used instead.
	 *
	 * @arg options A full set of Material dialog options with our dialog content configuration as the data property.
	 */
    openDialogFromTemplate (options: INoticeDialogOptions = {}): MatDialogRef<NgxNoticeDialogComponent> {
        const templateConfig: INoticeDialogOptions = {
            direction: undefined,
            viewContainerRef: undefined,
            autoFocus: true,
            disableClose: true,
            ...options
        };
        templateConfig.panelClass = this._utilities.mergePanelClass(options.panelClass, [ 'notifications-dialog' ]);

        return this._zone.run(() => {
            // Configure a dialog with our custom template, pass the Material options, sending template options as data.
            const dialogRef: MatDialogRef<NgxNoticeDialogComponent> = this._dialog.open(NgxNoticeDialogComponent, templateConfig);
            return dialogRef;
        });
    }

    /**
	 * Show a material based snack bar with an icon.
	 *
	 * This is a lower level method and in most cases `notify()` should be used instead.
	 */
    openIconSnackbarFromTemplate (
        options: INoticeIconSnackBarOptions = { data: { message: 'Complete' } }
    ): MatSnackBarRef<NgxNoticeInfoSnackbarComponent> {
        const config = { duration: 2000, ...options };
        return this._zone.run(() =>
        // We open a standard Material snackbar, but with our component as the body.
			 this._snackBar.openFromComponent(NgxNoticeInfoSnackbarComponent, config)
        );
    }

    /**
	 * Show a Material Snack Bar. This is the lowest level method and will not have any of the notification service customizations.
	 * In most cases `notify()` should be used instead.
	 *
	 * Ask yourself, "if we were to change the way we presented debug notifications in the future, should"
	 */
    openMaterialSnackbar = (options: INoticeSnackBarOptions = { message: 'Complete' }): MatSnackBarRef<SimpleSnackBar> => {
        let _config: INoticeDialogOptions = {};
        if (options.config) {
            _config = options.config;
        }

        const classes = this._utilities.mergePanelClass(_config.panelClass, [ 'ofInfoNotification', 'ofSnackbar' ]);

        const config = options.config ? options.config : {};
        const matConfig: MatSnackBarConfig = {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            ...config,
            panelClass: classes
        };
        if (!options.action) {
            matConfig.duration = 3000;
        }
        return this._zone.run(() => this._snackBar.open(options.message, options.action, matConfig));
    };
}

// export const showSnackbar = this.openMaterialSnackbar;
